// @ts-strict-ignore
import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl, NgModel, UntypedFormControl, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LabelIconData } from '../label-icon/label-icon.component.types';
@Component({
    selector: 'app-input-base',
    template: '',
    standalone: false
})
export class InputBaseComponent<T> implements ControlValueAccessor, OnDestroy, OnInit, DoCheck, AfterViewInit {
  @Input() autofocus: boolean = false;
  @Input() errorsMessages: ValidationErrors = {};
  @Input() errorMessage: string | Function = undefined;
  @Input() formControl: UntypedFormControl;
  @Input() formControlName: string;
  @Input() ignoreReadOnly: boolean = false;
  @Input() inputWidth: string = '100%';
  @Input() labelWidth: string = '100%';
  @Input() label: string;
  @Input() labelIcon?: LabelIconData;
  @Input() labelPositionLeft: boolean = false;
  @Input() labelSuffix: string;
  @Input() messagePad: string = '16px';
  @Input() required: boolean = false;
  /**
   * If set to false no error messages will show, the consumer is expected to handle the display of error messages.
   * The control will still be invalid if errors are present in the data.
   */
  @Input() showErrors: boolean = true;
  @Input() style: { [klass: string]: any };
  @Input() width: string = '100%';

  @ViewChild('model') model: NgModel;
  @ViewChild('input') input: ElementRef;

  @HostBinding('class.disabled') isDisabled = false;
  @HostBinding('class.messagePad') get addMessPad() {
    return this.messagePad !== '16px';
  }

  get hasErrors(): boolean {
    return !!this.control?.errors && this.showErrors;
  }
  get isDirty(): boolean {
    return this.control?.dirty || this.dirty;
  }

  get displayErrors(): boolean {
    return this.hasErrors && this.isDirty;
  }

  errors: string[];
  subscription: Subscription;
  value: T;
  pristine = true;
  dirty = false;
  constructor(@Self() @Optional() public control: NgControl) {
    this.control && (this.control.valueAccessor = this);
  }
  ngDoCheck(): void {
    if (this.pristine !== this.control?.pristine) {
      this.pristine = this.control?.pristine;
    }
    if (this.dirty !== this.control?.dirty) {
      this.dirty = this.control?.dirty;
    }
    this.checkValidation();
  }
  ngOnInit(): void {
    this.control?.control.updateValueAndValidity({ onlySelf: true });
    this.subscription = this.control?.valueChanges.subscribe((value) => {
      this.checkValidation();
    });
    this.checkValidation();
    if (this.labelSuffix) {
      this.label = this.label + this.labelSuffix;
    }
  }

  ngAfterViewInit(): void {
    if (this.autofocus) {
      this.focus();
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  checkValidation() {
    this.errors = [];
    this.model?.control.setErrors(this.control?.errors);

    if (this.control?.errors) {
      if (this.errorMessage) {
        if (this.errorMessage instanceof Function) {
          this._addError(this.errorMessage());
        } else {
          this._addError(this.errorMessage);
        }
      } else if (this.errorsMessages) {
        Object.keys(this.control?.errors).forEach((error) => {
          if (this.control?.errors[error] !== false) {
            if (Object.keys(this.errorsMessages).indexOf(error) > -1) {
              this._addError(this.errorsMessages[error]);
            } else {
              this._addError(error);
            }
          }
        });
      }
    }
  }

  focus() {
    this.input?.nativeElement.focus();
    setTimeout(() => {
      this.control?.control.updateValueAndValidity();
    });
  }

  onChange: (value: T) => void = () => {};

  onTouched: Function = () => {};

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.isDisabled = disabled;
  }

  writeValue(value: T) {
    this.value = value;
  }

  private _addError(error: string) {
    if (this.errors.indexOf(error) === -1) {
      this.errors.push(error);
    }
  }
}
